import moment from 'moment';
import React, { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, Tooltip, XAxis, YAxis } from 'recharts';
import { Title, cn } from 'rizzui';
import ScorePeriodCard from '../../../../../components/ScorePeriodCard';
import useFetch from '../../../../../hooks/useFetch';
import useFilters from '../../../../../hooks/useFilters';
import { useWindowDimensions } from '../../../../../hooks/useWindowDimensions';
import { UserService } from '../../../../../services/user/userService';
import UserProfileLayout from '../../UserProfileLayout';
import CustomTable from '../../../../../components/table/CustomTable';
import { Loader } from '../../../../../components/loader/SpinnerLogo';
import { usePrivilege } from '../../../../../components/priviledge/PriviledgeProvider';
import { menuRoutes } from '../../../../../router/menu';
import { Link } from 'react-router-dom';
import { Text } from 'rizzui';
import { FilterDrawerView } from '../../../../../components/table/components/TableFilter';
import ScoresFilters from './ScoresFilters';

interface CustomTooltipProps {
    active?: boolean;
    payload?: any;
    label?: string;
}

const columnOrder = ['contract', 'contractId', 'score', 'createdAt'];

export const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        return (
            <div className="bg-white p-3 rounded-lg shadow-lg border">
                <p className="font-medium text-gray-900">{label}</p>
                {payload.map((entry: any, index: number) => (
                    <p key={index} className="text-sm" style={{ color: entry.color }}>
                        {`${entry.name}: ${entry.value}`}
                    </p>
                ))}
            </div>
        );
    }
    return null;
};

interface UserScoresProps { }

const UserScores: React.FC<UserScoresProps> = () => {
    const { id = '' } = useParams<{ id: string }>();
    const { width } = useWindowDimensions();
    const { filters, updateFilters, resetFilters, updatePage, updatePageSize, updateFilterOrder } = useFilters({
        commercial: id,
        startDate: moment().subtract(6, 'months').format('YYYY-MM-DD'),
        endDate: moment().format('YYYY-MM-DD')
    });

    const [contractScores, setContractScores] = useState<any[]>([]);
    const [listScores, setListScores] = useState<any | null>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const { userCan } = usePrivilege();
    const [openFilters, setOpenFilters] = useState<boolean>(false);

    const [dailyScores] = useFetch(useCallback(async () => {
        if (!id || id === '') return null;
        const response = await (new UserService()).showUserScoresComparison(id, 'daily');
        return response.getResponseData();
    }, [id]));
    const [weeklyScores] = useFetch(useCallback(async () => {
        if (!id || id === '') return null;
        const response = await (new UserService()).showUserScoresComparison(id, 'weekly');
        return response.getResponseData();
    }, [id]));
    const [monthlyScores] = useFetch(useCallback(async () => {
        if (!id || id === '') return null;
        const response = await (new UserService()).showUserScoresComparison(id, 'monthly');
        return response.getResponseData();
    }, [id]));

    const fetchScores = async () => {
        setIsLoading(true);
        try {
            const response = await (new UserService()).listUserContractScores(filters);
            const responseData = response.getResponseData()?.data;

            // Guarda solo los registros para la tabla
            setContractScores(responseData?.data || []);

            // Guarda la respuesta completa (data, totalRegisters, lastPage…)
            setListScores(responseData);

            return responseData;
        } catch (error) {
            console.error('Error fetching user scores:', error);
            return null;
        } finally {
            setIsLoading(false);
        }
    };


    const orderedData = contractScores.map((row: any) => {
        const formattedRow: any = {
            contract: row.contract.code,
            contractId: row.contract.id,
            score: row.score,
            createdAt: moment(row.createdAt.date).format('DD/MM/YYYY'),
        };

        const orderedRow: any = {};
        columnOrder.forEach((column) => {
            orderedRow[column] = formattedRow[column];
        });

        return orderedRow;
    });

    React.useEffect(() => {
        fetchScores();
    }, [filters]);

    return (
        <UserProfileLayout >
            <div className="@container">
                {/* Score Period Panel */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 mt-6">
                    <ScorePeriodCard
                        title="Hoy (vs ayer)"
                        currentScore={dailyScores?.current || 0}
                        previousScore={dailyScores?.previous || 0}
                        isLoading={isLoading}
                    />
                    <ScorePeriodCard
                        title="Esta semana (vs semana anterior)"
                        currentScore={weeklyScores?.current || 0}
                        previousScore={weeklyScores?.previous || 0}
                        isLoading={isLoading}
                    />
                    <ScorePeriodCard
                        title="Este mes (vs mes anterior)"
                        currentScore={monthlyScores?.current || 0}
                        previousScore={monthlyScores?.previous || 0}
                        isLoading={isLoading}
                    />
                </div>

                {/* Bar chart of top 10 contracts by score */}

                <div className="mt-6 p-4 bg-white rounded-lg border border-muted">
                    <Title as="h3" className="text-base font-semibold sm:text-lg mb-4">
                        Puntuaciones por Contrato
                    </Title>

                    <CustomTable
                        id="user-contract-scores"
                        columnOrder={columnOrder}
                        data={orderedData}
                        isLoading={isLoading}
                        columnsNotShown={['contractId']}
                        overrideColumns={[
                            {
                                key: 'contract',
                                render: (row: any) => {
                                    return (
                                        userCan('get_contracts', 'contracts')
                                            ? (
                                                <Link to={`${menuRoutes.contracts.path}/${row.row.original.contractId}/profile${menuRoutes.contracts.profile.info}`}>
                                                    <Text className='font-bold text-primary'>{row.row.original.contract}</Text>
                                                </Link>
                                            )
                                            : <Text className='font-bold text-primary'>{row.row.original.contract}</Text>
                                    );
                                }
                            },
                            {
                                key: 'score',
                                render: (row: any) => {
                                    const value = row.row.original.score;
                                    const positive = value > 0;
                                    const negative = value < 0;

                                    const displayValue = positive ? `+${value}` : negative ? `-${Math.abs(value)}` : `${value}`;

                                    return (
                                        <span
                                            className={cn(
                                                "px-2 py-1 rounded-md font-semibold min-w-[60px] inline-block text-center",
                                                positive && "bg-green-100 text-green-700 border border-green-300",
                                                negative && "bg-red-100 text-red-700 border border-red-300",
                                                !positive && !negative && "bg-gray-100 text-gray-700 border border-gray-300"
                                            )}
                                        >
                                            {displayValue}
                                        </span>
                                    );
                                }
                            },
                        ]}
                        paginationData={{
                            pageSize: filters.limit,
                            currentPage: filters.page,
                            pageCount: listScores ? listScores.lastPage : 1,
                            totalCount: listScores?.totalRegisters,
                            handlePagination: updatePage,
                            handlePerPage: updatePageSize,
                        }}
                        filters={filters}
                        updateFilters={updateFilters}
                        updateFilterOrder={updateFilterOrder}
                        defaultOrder={filters.filter_order || undefined}
                        toggleFilters={() => setOpenFilters(!openFilters)}
                    />
                </div>

            </div>

            <FilterDrawerView isOpen={openFilters} setOpenDrawer={setOpenFilters} drawerTitle={'Filtros Puntuaciones'}>
                <div className="grid grid-cols-1 gap-6">
                    <ScoresFilters filters={filters} updateFilters={updateFilters} resetFilters={resetFilters} />
                </div>
            </FilterDrawerView>
        </UserProfileLayout>
    );
};

export default UserScores;